import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import { collection, query, orderBy, onSnapshot, addDoc, serverTimestamp, deleteDoc, doc } from "firebase/firestore";
import useCurrentUser from "../../hooks/useCurrentUser";
import {
    Megaphone,
    Image as ImageIcon,
    Calendar,
    Send,
    Trash2,
    Users,
    Building2,
    AlertCircle
} from "lucide-react";

export default function PlatformAnnouncements() {
    const { user } = useCurrentUser();
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);

    const [formData, setFormData] = useState({
        title: "",
        message: "",
        imageUrl: "",
        targetType: "allUsers",
        targetId: "",
        priority: "normal",
        expiresAt: ""
    });

    useEffect(() => {
        const q = query(collection(db, "announcements"), orderBy("createdAt", "desc"));
        const unsub = onSnapshot(q, (snap) => {
            const data = [];
            snap.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
            setAnnouncements(data);
            setLoading(false);
        });
        return () => unsub();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.title || !formData.message) return alert("Title and message are required.");

        try {
            const payload = {
                title: formData.title,
                message: formData.message,
                imageUrl: formData.imageUrl || null,
                targetType: formData.targetType,
                targetId: formData.targetType === 'tenant' ? formData.targetId : null,
                priority: formData.priority,
                expiresAt: formData.expiresAt ? new Date(formData.expiresAt) : null,
                createdAt: serverTimestamp(),
                createdBy: user?.uid || "super_admin",
                status: "active"
            };

            await addDoc(collection(db, "announcements"), payload);
            alert("Announcement published successfully!");
            setFormData({
                title: "",
                message: "",
                imageUrl: "",
                targetType: "allUsers",
                targetId: "",
                priority: "normal",
                expiresAt: ""
            });
        } catch (error) {
            console.error("Error publishing announcement:", error);
            alert("Failed to publish announcement.");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this announcement?")) return;
        try {
            await deleteDoc(doc(db, "announcements", id));
        } catch (error) {
            console.error("Error deleting announcement:", error);
        }
    };

    return (
        <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
            {/* Header */}
            <div>
                <h1 className="text-3xl font-bold text-navy-900 font-serif flex items-center gap-3">
                    <Megaphone className="text-navy-900" size={32} />
                    Platform Announcements
                </h1>
                <p className="text-slate-500 mt-1">Broadcast notifications to tenants, users, or the entire platform.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Create Form */}
                <div className="lg:col-span-1 border-r border-slate-200 pr-8">
                    <div className="bg-white rounded-3xl border border-slate-200 p-6 shadow-sm sticky top-8">
                        <h2 className="text-lg font-bold text-navy-900 flex items-center gap-2 mb-6">
                            <Send size={18} /> New Broadcast
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4 text-sm">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Title</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-navy-900/20"
                                    placeholder="e.g., Scheduled Maintenance"
                                    value={formData.title}
                                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Message</label>
                                <textarea
                                    required
                                    rows={4}
                                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-navy-900/20 resize-none"
                                    placeholder="Enter full announcement details..."
                                    value={formData.message}
                                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2">
                                    <ImageIcon size={14} /> Image URL (Optional)
                                </label>
                                <input
                                    type="url"
                                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-navy-900/20"
                                    placeholder="https://example.com/image.png"
                                    value={formData.imageUrl}
                                    onChange={e => setFormData({ ...formData, imageUrl: e.target.value })}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Audience</label>
                                    <select
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-navy-900/20 font-bold text-navy-900"
                                        value={formData.targetType}
                                        onChange={e => setFormData({ ...formData, targetType: e.target.value })}
                                    >
                                        <option value="allUsers">All Users</option>
                                        <option value="allTenants">All Tenants</option>
                                        <option value="tenant">Specific Tenant</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Priority</label>
                                    <select
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-navy-900/20 font-bold"
                                        value={formData.priority}
                                        onChange={e => setFormData({ ...formData, priority: e.target.value })}
                                    >
                                        <option value="normal" className="text-blue-600">Normal</option>
                                        <option value="important" className="text-rose-600">Important</option>
                                    </select>
                                </div>
                            </div>

                            {formData.targetType === 'tenant' && (
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2">
                                        <Building2 size={14} /> Tenant ID / Name
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        className="w-full border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-navy-900/20"
                                        placeholder="e.g., Decathlon"
                                        value={formData.targetId}
                                        onChange={e => setFormData({ ...formData, targetId: e.target.value })}
                                    />
                                </div>
                            )}

                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase mb-1 flex items-center gap-2">
                                    <Calendar size={14} /> Expiration Date (Optional)
                                </label>
                                <input
                                    type="datetime-local"
                                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:ring-2 focus:ring-navy-900/20"
                                    value={formData.expiresAt}
                                    onChange={e => setFormData({ ...formData, expiresAt: e.target.value })}
                                />
                            </div>

                            <button
                                type="submit"
                                className="w-full mt-6 py-3 bg-navy-900 text-white rounded-xl font-bold hover:bg-navy-800 transition-all shadow-lg flex justify-center items-center gap-2"
                            >
                                <Megaphone size={18} /> Publish Broadcast
                            </button>
                        </form>
                    </div>
                </div>

                {/* Listing */}
                <div className="lg:col-span-2 space-y-6">
                    <h2 className="text-xl font-bold text-navy-900 border-b border-slate-200 pb-2">Active Announcements</h2>
                    {loading ? (
                        <div className="text-center py-12 text-slate-400 font-medium">Loading history...</div>
                    ) : announcements.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-3xl border border-slate-200 text-slate-400 font-medium">
                            No announcements published yet.
                        </div>
                    ) : (
                        announcements.map(item => (
                            <div key={item.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex gap-4 transition-all hover:bg-slate-50">
                                {item.imageUrl ? (
                                    <img src={item.imageUrl} alt="banner" className="w-24 h-24 object-cover rounded-xl bg-slate-100 shrink-0 border border-slate-200" />
                                ) : (
                                    <div className="w-12 h-12 bg-slate-100 rounded-xl shrink-0 flex items-center justify-center border border-slate-200 text-slate-400">
                                        {item.priority === 'important' ? <AlertCircle size={20} className="text-rose-500" /> : <Megaphone size={20} />}
                                    </div>
                                )}
                                <div className="flex-1">
                                    <div className="flex justify-between items-start">
                                        <div className="flex items-center gap-2 mb-1">
                                            {item.priority === 'important' && (
                                                <span className="bg-rose-100 text-rose-700 text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-rose-200">
                                                    Important
                                                </span>
                                            )}
                                            <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-100 px-2 py-0.5 rounded-full border border-slate-200">
                                                {item.targetType === 'allUsers' ? 'All Users' : item.targetType === 'allTenants' ? 'All Tenants' : `Tenant: ${item.targetId}`}
                                            </span>
                                        </div>
                                        <button onClick={() => handleDelete(item.id)} className="text-slate-400 hover:text-red-600 transition-colors p-1" title="Delete">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                    <h3 className="font-bold text-navy-900 text-lg mb-1">{item.title}</h3>
                                    <p className="text-sm text-slate-600 leading-relaxed mb-3">{item.message}</p>
                                    <div className="flex items-center gap-4 text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                                        <span>Published: {item.createdAt?.toDate ? item.createdAt.toDate().toLocaleDateString() : 'N/A'}</span>
                                        {item.expiresAt?.toDate && (
                                            <span className="text-amber-600">Expires: {item.expiresAt.toDate().toLocaleDateString()}</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
